import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';

const { 
  FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiTarget, FiArrowRight, FiZap,
  FiCalendar, FiDatabase, FiFileText, FiBarChart2, FiAward, FiEdit, FiDownload,
  FiLayout, FiUsers, FiAlertTriangle
} = FiIcons;

const FeatureCard = ({ icon, title, description, color }) => (
  <motion.div
    whileHover={{ y: -5, scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-5 border-t border-l border-white`}
  >
    <div className={`${color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-md`}>
      <SafeIcon icon={icon} className="text-white text-xl" />
    </div>
    <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </motion.div>
);

const Login = () => {
  const { login, loginWithGoogle, authLoading, authError } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showFeatures, setShowFeatures] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  
  // Update error state when authError changes
  useEffect(() => {
    if (authError) {
      setSubmitError(authError);
    }
  }, [authError]);

  // Set demo credentials on mount for easier access
  useEffect(() => {
    setFormData({
      ...formData,
      email: 'demo@fahim.com',
      password: 'demo123'
    });
  }, []);
  
  const features = [
    {
      icon: FiDatabase,
      title: "Question Bank",
      description: "Create, organize, and manage your question repository with advanced filtering and categorization.",
      color: "bg-emerald-500"
    },
    {
      icon: FiFileText,
      title: "Custom Templates",
      description: "Design professional exam templates with your institution's branding and formatting preferences.",
      color: "bg-violet-500"
    },
    {
      icon: FiZap,
      title: "Quick Generation",
      description: "Generate balanced exam papers in seconds based on learning outcomes and difficulty levels.",
      color: "bg-purple-500"
    },
    {
      icon: FiCalendar,
      title: "Exam Calendar",
      description: "Schedule and track exams with attendance monitoring and comprehensive exam management.",
      color: "bg-orange-500"
    },
    {
      icon: FiBarChart2,
      title: "Analytics & Insights",
      description: "Gain valuable insights into exam performance, question effectiveness, and student outcomes.",
      color: "bg-blue-500"
    },
    {
      icon: FiAward,
      title: "Learning Outcomes",
      description: "Align questions with course learning outcomes and Bloom's taxonomy for better assessment.",
      color: "bg-indigo-500"
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isLoginMode && !formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!isLoginMode && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    
    if (validateForm()) {
      try {
        const result = await login({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          isSignup: !isLoginMode
        });
        
        if (!result.success) {
          setSubmitError(result.message);
        }
      } catch (error) {
        setSubmitError(error.message || 'An error occurred during login');
        console.error('Login error:', error);
      }
    }
  };

  const handleGoogleLogin = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    
    try {
      const result = await loginWithGoogle();
      if (!result.success) {
        setSubmitError(result.message);
      }
    } catch (error) {
      setSubmitError(error.message || 'An error occurred during Google login');
      console.error('Google login error:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear submit error when user starts typing again
    if (submitError) {
      setSubmitError(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-0 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
        </div>
      </div>

      <div className="w-full max-w-7xl flex flex-col md:flex-row items-center justify-center md:justify-between gap-8 md:gap-4 p-6 relative z-10">
        {/* Left Side: Features Showcase */}
        <AnimatePresence>
          {showFeatures && (
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="w-full md:w-3/5 space-y-8 hidden md:block"
            >
              <div className="text-center md:text-left">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
                  Revolutionize Your <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                    Exam Management
                  </span>
                </h1>
                <p className="text-slate-300 text-lg max-w-xl mx-auto md:mx-0">
                  FAHIM streamlines the entire assessment process from question creation to exam generation and scheduling.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <FeatureCard {...feature} />
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-wrap gap-6 justify-center md:justify-start">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center space-x-3">
                  <div className="bg-green-500 w-10 h-10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiEdit} className="text-white" />
                  </div>
                  <div className="text-white">
                    <p className="font-medium">1,000+</p>
                    <p className="text-xs opacity-80">Questions Created</p>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center space-x-3">
                  <div className="bg-blue-500 w-10 h-10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiDownload} className="text-white" />
                  </div>
                  <div className="text-white">
                    <p className="font-medium">500+</p>
                    <p className="text-xs opacity-80">Papers Generated</p>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center space-x-3">
                  <div className="bg-purple-500 w-10 h-10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUsers} className="text-white" />
                  </div>
                  <div className="text-white">
                    <p className="font-medium">50+</p>
                    <p className="text-xs opacity-80">Institutions</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Right Side: Login Form */}
        <div className="w-full md:w-2/5 max-w-md">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <motion.div
              className="bg-gradient-to-br from-purple-600 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <SafeIcon icon={FiTarget} className="text-white text-3xl" />
            </motion.div>
            <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">FAHIM</h1>
            <p className="text-slate-300 text-lg mb-2">Flexible Assessment Hub</p>
            <p className="text-slate-400 text-sm">for Innovative Measurement</p>
          </motion.div>

          {/* Login/Signup Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8"
          >
            {/* Error Message */}
            <AnimatePresence>
              {submitError && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg"
                >
                  <div className="flex items-start">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-500 mt-0.5 mr-2" />
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Google Login Button */}
            <motion.button
              onClick={handleGoogleLogin}
              disabled={authLoading}
              className="w-full bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-sm hover:shadow-md group mb-6"
              whileHover={{ scale: authLoading ? 1 : 1.02 }}
              whileTap={{ scale: authLoading ? 1 : 0.98 }}
            >
              <div className="flex items-center justify-center space-x-3">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </div>
            </motion.button>

            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="px-4 text-sm text-gray-500 bg-white">or</span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            {/* Tab Switcher */}
            <div className="flex mb-8 bg-slate-100 rounded-2xl p-1">
              <button
                onClick={() => setIsLoginMode(true)}
                className={`flex-1 py-3 px-4 text-center font-semibold transition-all duration-300 rounded-xl ${
                  isLoginMode 
                    ? 'bg-white text-slate-900 shadow-lg' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLoginMode(false)}
                className={`flex-1 py-3 px-4 text-center font-semibold transition-all duration-300 rounded-xl ${
                  !isLoginMode 
                    ? 'bg-white text-slate-900 shadow-lg' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field (Signup only) */}
              {!isLoginMode && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-slate-700">
                    Full Name
                  </label>
                  <div className="relative group">
                    <SafeIcon 
                      icon={FiUser} 
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" 
                    />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full pl-12 pr-4 py-4 bg-slate-50 border-2 rounded-xl focus:bg-white focus:border-purple-500 focus:outline-none transition-all duration-300 text-slate-900 placeholder-slate-400 ${
                        errors.name ? 'border-red-400 bg-red-50' : 'border-slate-200'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.name && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-500 text-sm font-medium"
                    >
                      {errors.name}
                    </motion.p>
                  )}
                </motion.div>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Email Address
                </label>
                <div className="relative group">
                  <SafeIcon 
                    icon={FiMail} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" 
                  />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-12 pr-4 py-4 bg-slate-50 border-2 rounded-xl focus:bg-white focus:border-purple-500 focus:outline-none transition-all duration-300 text-slate-900 placeholder-slate-400 ${
                      errors.email ? 'border-red-400 bg-red-50' : 'border-slate-200'
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm font-medium"
                  >
                    {errors.email}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <div className="relative group">
                  <SafeIcon 
                    icon={FiLock} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" 
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full pl-12 pr-12 py-4 bg-slate-50 border-2 rounded-xl focus:bg-white focus:border-purple-500 focus:outline-none transition-all duration-300 text-slate-900 placeholder-slate-400 ${
                      errors.password ? 'border-red-400 bg-red-50' : 'border-slate-200'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm font-medium"
                  >
                    {errors.password}
                  </motion.p>
                )}
              </div>

              {/* Confirm Password Field (Signup only) */}
              {!isLoginMode && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-slate-700">
                    Confirm Password
                  </label>
                  <div className="relative group">
                    <SafeIcon 
                      icon={FiLock} 
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" 
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`w-full pl-12 pr-4 py-4 bg-slate-50 border-2 rounded-xl focus:bg-white focus:border-purple-500 focus:outline-none transition-all duration-300 text-slate-900 placeholder-slate-400 ${
                        errors.confirmPassword ? 'border-red-400 bg-red-50' : 'border-slate-200'
                      }`}
                      placeholder="Confirm your password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-500 text-sm font-medium"
                    >
                      {errors.confirmPassword}
                    </motion.p>
                  )}
                </motion.div>
              )}

              {/* Forgot Password (Login only) */}
              {isLoginMode && (
                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-purple-600 hover:text-purple-700 font-semibold transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={authLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 focus:ring-4 focus:ring-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl group"
                whileHover={{ scale: authLoading ? 1 : 1.02 }}
                whileTap={{ scale: authLoading ? 1 : 0.98 }}
              >
                {authLoading ? (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{isLoginMode ? 'Signing In...' : 'Creating Account...'}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span>{isLoginMode ? 'Sign In' : 'Create Account'}</span>
                    <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </motion.button>
            </form>

            {/* Demo Credentials */}
            <div className="mt-6 p-5 bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Demo Credentials
              </h4>
              <div className="text-sm text-slate-600 space-y-1 mb-3">
                <p className="font-mono">demo@fahim.com</p>
                <p className="font-mono">demo123</p>
              </div>
              <button
                onClick={() => {
                  setFormData({
                    ...formData,
                    email: 'demo@fahim.com',
                    password: 'demo123'
                  });
                  setIsLoginMode(true);
                  setSubmitError(null);
                }}
                className="text-purple-600 hover:text-purple-700 font-semibold text-sm transition-colors hover:underline"
              >
                Use Demo Credentials →
              </button>
            </div>

            {/* Mobile Features Toggle */}
            <div className="mt-6 text-center md:hidden">
              <button
                onClick={() => setShowFeatures(!showFeatures)}
                className="text-purple-600 hover:text-purple-700 font-semibold text-sm transition-colors underline"
              >
                {showFeatures ? 'Hide Features' : 'Show FAHIM Features'}
              </button>
              
              {/* Mobile Features Display */}
              <AnimatePresence>
                {showFeatures && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <h3 className="text-lg font-bold text-slate-800">Key Features</h3>
                    <div className="space-y-3">
                      {features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                          <div className={`${feature.color} w-8 h-8 rounded-lg flex items-center justify-center`}>
                            <SafeIcon icon={feature.icon} className="text-white text-sm" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-slate-800">{feature.title}</h4>
                            <p className="text-xs text-slate-600">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-8 text-slate-300"
          >
            <p>© 2025 FAHIM. Empowering Global Assessment Innovation ❤️</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;